import { AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { GetRowIdParams, IServerSideDatasource, ValueGetterParams } from 'ag-grid-enterprise';
import { Subject, Observable } from 'rxjs';
import { getCellRenderer, getValueGetter } from 'src/app/modules/core/components/ag-grid';
import { takeUntil, take, map } from 'rxjs/operators';
import { GridHelperService } from 'src/app/modules/core/services/grid-helper.service';
import { DataTableServerComponent } from 'src/app/modules/core/components/data-table-server/data-table.component';
import { DocumentReader, FieldDataType, SchemaField, SystemFieldType } from 'src/app/modules/core/store/models';
import { QueryParams } from 'src/app/modules/core/store/models/query.model';
import { PicklistService } from 'src/app/modules/core/services/picklist/picklist.service';
import * as fromRouting from 'src/app/modules/core/store/routing';
import * as fromComm from 'src/app/modules/communications/store/communications';
import { MassNotificationTemplate } from 'src/app/modules/communications/store/communications/communications.model';
import { CommunicationsApiService } from 'src/app/modules/communications/store/communications/communications-api.service';
import * as fromAuth from 'src/app/modules/authentication/store/auth/auth.selectors';
import { v4 as uuidv4 } from 'uuid';
import { UserContext, Permissions } from 'src/app/modules/authentication/store/auth';
import { OrgBusinessUnitsApiService } from '@app/modules/core/org-business-units/org-business-units-api.service';
import { Dialog } from '@angular/cdk/dialog';
import { TemplateGroupsCRUDModalComponent } from '@app/modules/communications/components/template-groups-crud-modal/template-groups-crud-modal.component';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';
import { UserContextService } from '@app/modules/core/services/user-context.service';

@Component({
  selector: 'topo-comms-template-list',
  templateUrl: './template-list.component.html',
  styleUrls: ['./template-list.component.scss'],
  standalone: false
})
export class TemplateListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(DataTableServerComponent, { static: true }) dataTable: DataTableServerComponent;

  destroy$ = new Subject<void>();
  notificationTemplateToDelete: MassNotificationTemplate;
  queryParams$: Observable<QueryParams>;
  datasource: any;
  columDefs: any[] = [];
  skip: number = 0;
  take: number = 10;
  totalCount: number = 0;
  isTableLoading: boolean;
  cannnotDeleteMessage: string;
  showCannnotDelete: boolean;
  userContext: UserContext;
  hasBusinessUnits: boolean = true;
  hasTemplateGroups: boolean = false;
  canAddTemplate: boolean;
  Permissions = Permissions;

  protected readonly showTemplateEdit$ = this.store
    .select(fromComm.selectSelectedMassNotificationTemplate)
    .pipe(map(template => !!template));

  protected readonly userContextService = inject(UserContextService);
  private notify = inject(SystemNotificationsService);

  constructor(
    private store: Store<fromComm.State>,
    private translateService: TranslateService,
    private gridHelper: GridHelperService,
    private commApiService: CommunicationsApiService,
    private picklistService: PicklistService,
    private orgBusinessUnitsApiService: OrgBusinessUnitsApiService,
    private dialog: Dialog
  ) {}

  ngOnInit(): void {
    if (!this.userContextService.canReadCommunicationTemplate()) {
      this.notify.error(this.translateService.instant('COMMON.MISSING-PERMISSIONS'));
      return;
    }

    this.store
      .select(fromComm.selectIsdMassNotificationTemplatesTableChanged)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isChanged => {
        if (isChanged === true) {
          this.dataTable?.refreshGrid();
        }
      });

    this.orgBusinessUnitsApiService.hasOrgBusinessUnits().subscribe(hasBusinessUnits => {
      this.hasBusinessUnits = hasBusinessUnits;
      this.canAddTemplate = !!!this.hasBusinessUnits || !!this.userContext?.businessUnitUId;
    });

    this.commApiService.hasMassNotificationTemplateGroups().subscribe(hasTemplateGroups => {
      this.hasTemplateGroups = hasTemplateGroups;
      this.columDefs = this.generateColumns();
      this.dataTable?.refreshGrid();
    });

    this.store
      .select(fromAuth.selectUserContext)
      .pipe(takeUntil(this.destroy$))
      .subscribe(userContext => {
        if (!!userContext) {
          this.userContext = userContext;
          this.canAddTemplate = !!!this.hasBusinessUnits || !!this.userContext?.businessUnitUId;
        }
      });

    this.store
      .select(fromAuth.selectPermissionsChangeTime)
      .pipe(takeUntil(this.destroy$))
      .subscribe(time => {
        if (!!time) {
          this.columDefs = this.generateColumns();
          this.dataTable?.refreshGrid();
        }
      });

    this.columDefs = this.generateColumns();

    this.datasource = this.getServerSideDatasource();
  }

  ngAfterViewInit() {
    this.dataTable?.groupRows(false);
  }

  ngOnDestroy(): void {
    this.gridHelper.clearSearchFilter();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private generateColumns() {
    var columns = [];

    var actionsColumn = {
      colId: '_actions',
      headerName: '',
      field: '',
      floatingFilter: false,
      sortable: false,
      resizable: false,
      minWidth: 64,
      suppressHeaderMenuButton: true,
      cellRenderer: 'actionCellComponent',
      cellRendererParams: {
        location: 'right',
        actions: []
      },
      width: 64,
      maxWidth: 64
    };

    if (this.userContext?.hasPermission(Permissions.CommunicationTemplateUpdate)) {
      actionsColumn.cellRendererParams.actions.push({
        label: this.translateService.instant('COMMUNICATIONS.BUTTONS.EDIT'),
        icon: 'icon-edit',
        click: data => {
          this.editClick(data);
        }
      });
    }
    if (this.userContext?.hasPermission(Permissions.CommunicationTemplateCreate)) {
      actionsColumn.cellRendererParams.actions.push({
        label: this.translateService.instant('MANAGEMENT.COMMUNICATION-CENTER.COPY'),
        icon: 'icon-copy_duplicate',
        click: data => {
          this.copyClick(data);
        }
      });
    }
    if (this.userContext?.hasPermission(Permissions.CommunicationTemplateDelete)) {
      actionsColumn.cellRendererParams.actions.push({
        label: this.translateService.instant('COMMUNICATIONS.BUTTONS.DELETE'),
        icon: 'icon-delete_trash',
        click: data => {
          this.deleteClick(data);
        }
      });
    }

    if (actionsColumn.cellRendererParams.actions?.length) {
      columns.push(actionsColumn);
    }

    const nameField = {
      dataType: FieldDataType.Text,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.NAME'),
      systemName: 'name',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    const canEdit = this.userContext?.hasPermission(Permissions.CommunicationTemplateUpdate);

    columns.push({
      headerName: nameField.displayName,
      field: nameField.systemName,
      colId: 'name',
      sortable: true,
      resizable: true,
      width: 400,
      menuTabs: [],
      cellRenderer: canEdit ? 'linkCellComponent' : getCellRenderer(FieldDataType.Text),
      cellRendererParams: canEdit
        ? {
            schemaField: nameField,
            picklistService: null,
            clicked: data => {
              this.editClick(data);
            }
          }
        : { schemaField: nameField },
      valueGetter: getValueGetter(FieldDataType.Text)
    });

    const notificationType = {
      dataType: FieldDataType.Picklist,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.TYPE'),
      systemName: 'type',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: 'COMMUNICATIONS.COLUMNS.TYPE'
    } as SchemaField;

    columns.push({
      headerName: notificationType.displayName,
      field: notificationType.systemName,
      colId: 'type',
      sortable: true,
      resizable: true,
      width: 200,
      menuTabs: [],
      cellRenderer: 'enumTranslateCellComponent',
      cellRendererParams: {
        schemaField: notificationType,
        picklistService: this.picklistService
      },
      valueGetter: this.getNotificationTypeValue.bind(this)
    });

    if (this.hasTemplateGroups) {
      const templateGroup = {
        dataType: FieldDataType.Text,
        displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.TEMPLATE-GROUP'),
        systemName: 'templateGroupName',
        uId: uuidv4(),
        isUniqueKey: true,
        fieldType: SystemFieldType.System,
        picklistValues: [],
        sourceUnit: null,
        settings: 'COMMUNICATIONS.COLUMNS.TEMPLATE-GROUP'
      } as SchemaField;

      columns.push({
        headerName: templateGroup.displayName,
        field: templateGroup.systemName,
        colId: 'templateGroupName',
        sortable: true,
        resizable: true,
        width: 200,
        menuTabs: [],
        cellRenderer: getCellRenderer(FieldDataType.Text),
        cellRendererParams: {
          schemaField: templateGroup,
          picklistService: this.picklistService
        },
        valueGetter: this.getTemplateGroupValue.bind(this)
      });
    }

    const updatedOn = {
      dataType: FieldDataType.DateTime,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.UPDATED-ON'),
      systemName: 'updatedOn',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    columns.push({
      headerName: updatedOn.displayName,
      field: updatedOn.systemName,
      colId: 'updatedOn',
      sortable: true,
      resizable: true,
      width: 200,
      menuTabs: [],
      cellRenderer: getCellRenderer(FieldDataType.DateTime),
      cellRendererParams: {
        schemaField: updatedOn,
        picklistService: this.picklistService
      },
      valueGetter: getValueGetter(FieldDataType.DateTime)
    });

    const editedBy = {
      dataType: FieldDataType.User,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.UPDATED-BY'),
      systemName: 'lastEditedById',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    columns.push({
      headerName: editedBy.displayName,
      field: editedBy.systemName,
      colId: 'lastEditedById',
      sortable: true,
      resizable: true,
      width: 200,
      menuTabs: [],
      cellRenderer: getCellRenderer(FieldDataType.User),
      cellRendererParams: {
        schemaField: editedBy,
        nameFieldName: 'lastEditedByName',
        picklistService: this.picklistService
      },
      valueGetter: getValueGetter(FieldDataType.User)
    });

    const isActive = {
      dataType: FieldDataType.Boolean,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.IS-ACTIVE'),
      systemName: 'isActive',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    columns.push({
      headerName: isActive.displayName,
      field: isActive.systemName,
      colId: 'isActive',
      sortable: true,
      resizable: true,
      width: 200,
      menuTabs: [],
      cellRenderer: getCellRenderer(FieldDataType.Boolean),
      cellRendererParams: {
        schemaField: isActive,
        picklistService: this.picklistService,
        isEditable: this.userContext?.hasPermission(Permissions.CommunicationTemplateUpdate),
        click: data => {
          if (this.userContext?.hasPermission(Permissions.CommunicationTemplateUpdate)) {
            this.isActiveClick(data);
          }
        }
      },
      valueGetter: getValueGetter(FieldDataType.Boolean)
    });

    return columns;
  }

  newTemplate() {
    this.store.dispatch(fromComm.newMassNotificationTemplate());
  }

  navigate(path: string) {
    this.store.dispatch(
      fromRouting.go({
        routeData: { path: [path] }
      })
    );
  }

  editClick(template: MassNotificationTemplate) {
    this.store.dispatch(fromComm.editMassNotificationTemplate({ template }));
  }

  copyClick(template: MassNotificationTemplate) {
    this.store.dispatch(fromComm.copyMassNotificationTemplate({ template }));
  }

  deleteClick(template: MassNotificationTemplate) {
    this.commApiService.getMassNotificationTemplateUsageCount(template.uId).subscribe(res => {
      if (!!res) {
        this.showCannnotDelete = true;
        this.cannnotDeleteMessage = this.translateService.instant('COMMUNICATIONS.CANNOT-DELETE-TEMPLATE-MESSAGE', { number: res });
      } else {
        this.notificationTemplateToDelete = template;
      }
    });
  }

  getNotificationTypeValue = (params: ValueGetterParams) => {
    const field = params.colDef.cellRendererParams.schemaField;

    const reader = new DocumentReader(params.data);
    const value = reader.getFieldValue(field.systemName);

    return this.translateService.instant(`COMMUNICATIONS.NOTIFICATION-TYPE-${value}`);
  };

  getTemplateGroupValue = (params: ValueGetterParams) => {
    const groupField = params.colDef.cellRendererParams.schemaField;

    const groupReader = new DocumentReader(params.data);
    return groupReader.getFieldValue(groupField.systemName);
  };

  confirmDeleteNotificationTemplate(response: Boolean) {
    if (response) {
      this.store.dispatch(fromComm.deleteMassNotificationTemplate({ template: this.notificationTemplateToDelete }));
    }
    this.notificationTemplateToDelete = null;
  }

  onPaginate(skip: number): void {
    this.skip = skip;
  }

  onTakeChanged(take: number): void {
    this.take = take;
  }

  isActiveClick(template: MassNotificationTemplate): void {
    const active = template.isActive === true ? false : true;
    this.store.dispatch(fromComm.setMassNotificationTemplateActive({ active, template }));
  }

  onGridDataLoaded(): void {
    this.isTableLoading = false;
  }

  onConfirmCannotDelete(response: boolean): void {
    this.showCannnotDelete = false;
  }

  getServerSideDatasource(): IServerSideDatasource {
    this.isTableLoading = true;
    return {
      getRows: params => {
        var translatedParams = this.gridHelper.gridParamsToRequestParams(params.request);

        let sortField = 'UpdatedOn';
        let sortDirection = 'Descending';
        if (params?.request.sortModel?.length > 0) {
          sortField = params?.request.sortModel[0].colId;
          sortDirection = params?.request.sortModel[0].sort === 'asc' ? 'Ascending' : 'Descending';
        }

        const query = {
          skip: this.skip,
          take: this.take,
          sortField: sortField,
          sortDirection: sortDirection,
          where: translatedParams.where
        };

        this.commApiService.getMassNotificationTemplates(query, false, true).subscribe(
          resp => {
            const response = this.gridHelper.queryResultToGridData(resp);
            this.totalCount = response?.rowCount;

            this.store.dispatch(fromComm.setMassNotificationTemplatesTableChanged({ changed: false }));

            params.success({
              rowData: response?.rowData,
              rowCount: response?.rowCount
            });
          },
          err => params.fail()
        );
      }
    };
  }

  getRowId = (params: GetRowIdParams): string => {
    return params.data.uId;
  };

  openTemplateGroupDialog(): void {
    this.dialog.open(TemplateGroupsCRUDModalComponent, {
      autoFocus: false,
      width: '100%',
      maxWidth: '600px',
      disableClose: true
    });
  }
}
